<template>
  <v-app>
    <v-container fluid class="py-8">
      <v-row justify="center">
        <v-col cols="12" xl="10">
          <div class="mb-6">
            <h2 class="mb-1">Pesquisa de médicos — CNES</h2>
            <p class="text--secondary mb-0">
              Base pública do DATASUS (CNES).
              <template v-if="snapshot">
                Dados de <strong>{{ snapshotLegivel }}</strong
                >, que é o mês mais recente publicado.
              </template>
              <template v-if="totalCarregado">
                {{ totalCarregado.toLocaleString("pt-BR") }} médicos carregados.
              </template>
            </p>
          </div>

          <!-- ─── Filtros ─────────────────────────────────────────────── -->
          <v-card class="mb-6">
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filtros.uf"
                    :items="opcoes.ufs"
                    label="UF"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    @change="aoTrocarUf"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-combobox
                    v-model="filtros.municipio"
                    :items="opcoes.regioes.municipios || []"
                    label="Município"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="!filtros.uf"
                    :hint="!filtros.uf ? 'Escolha a UF primeiro' : ''"
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filtros.nivel_regiao"
                    :items="niveisRegiao"
                    label="Agrupar região por"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="!filtros.uf"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-combobox
                    v-model="filtros.regiao"
                    :items="regioesDoNivel"
                    label="Região"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="!filtros.uf"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model="filtros.nome"
                    label="Nome do médico"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    @keyup.enter="buscar(1)"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model="filtros.especialidade"
                    label="Especialidade"
                    placeholder="cardiologista"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    @keyup.enter="buscar(1)"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="filtros.crm"
                    label="CRM"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                    @keyup.enter="buscar(1)"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="4" class="d-flex align-center">
                  <v-checkbox
                    v-model="filtros.apenas_nao_sus"
                    label="Só quem atende fora do SUS"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                  />
                </v-col>
              </v-row>

              <div class="d-flex align-center mt-4">
                <v-btn color="primary" :loading="carregando" @click="buscar(1)">
                  <v-icon left>mdi-magnify</v-icon> Buscar
                </v-btn>
                <v-btn text class="ml-2" :disabled="carregando" @click="limpar">
                  Limpar
                </v-btn>
                <v-spacer />
                <span v-if="meta.total" class="text--secondary">
                  {{ meta.total.toLocaleString("pt-BR") }}
                  {{ meta.total === 1 ? "médico" : "médicos" }}
                </span>
              </div>
            </v-card-text>
          </v-card>

          <v-alert v-if="erro" type="error" text dense class="mb-4">
            {{ erro }}
          </v-alert>

          <!-- ─── Resultados ──────────────────────────────────────────── -->
          <v-card v-if="medicos.length">
            <v-simple-table>
              <template #default>
                <thead>
                  <tr>
                    <th>Médico</th>
                    <th>CRM</th>
                    <th>Especialidade (CBO)</th>
                    <th>Onde atua</th>
                    <th class="text-center">Vínculos</th>
                    <th class="text-center">Atende</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="medico in medicos" :key="medico.id">
                    <td>
                      <div class="font-weight-medium">{{ medico.name }}</div>
                      <div v-if="ddd(medico)" class="text-caption text--secondary">
                        DDD {{ ddd(medico) }}
                      </div>
                    </td>
                    <td class="text-no-wrap">{{ medico.crm }}/{{ medico.uf }}</td>
                    <td>
                      <v-chip
                        v-for="esp in medico.specialties || []"
                        :key="esp"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ esp.replace(/^Médico /, "") }}
                      </v-chip>
                    </td>
                    <td>
                      <div v-for="local in medico.locations || []" :key="local.id">
                        {{ local.municipality }}
                        <span v-if="local.metro_region" class="text-caption text--secondary">
                          — {{ local.metro_region }}
                        </span>
                      </div>
                    </td>
                    <td class="text-center">
                      {{ medico.links_count }}
                      <div class="text-caption text--secondary">
                        {{ medico.weekly_hours }}h
                      </div>
                    </td>
                    <td class="text-center text-no-wrap">
                      <v-chip v-if="medico.serves_non_sus" x-small color="primary" dark class="mr-1">
                        Particular
                      </v-chip>
                      <v-chip v-if="medico.serves_sus" x-small outlined>SUS</v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-divider />
            <div class="d-flex justify-center py-3">
              <v-pagination
                v-if="meta.ultima > 1"
                v-model="pagina"
                :length="meta.ultima"
                :total-visible="7"
                @input="buscar"
              />
            </div>
          </v-card>

          <v-card v-else-if="buscou && !carregando" class="pa-8 text-center">
            <v-icon size="48" color="grey lighten-1">mdi-account-search-outline</v-icon>
            <p class="mt-3 mb-0 text--secondary">
              Nenhum médico encontrado com esses filtros.
            </p>
          </v-card>

          <!-- Limite honesto da fonte: o comercial precisa saber o que NÃO tem. -->
          <v-alert v-if="medicos.length" type="info" text dense class="mt-4 text-caption">
            O CNES não traz telefone, e-mail, endereço nem CPF do médico — só o
            vínculo com o estabelecimento. A especialidade é a ocupação declarada
            no vínculo (CBO), não o registro no CFM.
          </v-alert>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { buscarMedicos, carregarOpcoes } from "@/services/cnesService";

const FILTROS_VAZIOS = {
  uf: null,
  municipio: null,
  regiao: null,
  nivel_regiao: "metropolitana",
  nome: null,
  especialidade: null,
  crm: null,
  apenas_nao_sus: false,
};

export default {
  name: "CnesSearchPage",

  data() {
    return {
      filtros: { ...FILTROS_VAZIOS },
      opcoes: { ufs: [], niveis_regiao: [], regioes: {}, snapshot: null, total_medicos: 0 },
      medicos: [],
      meta: { total: 0, pagina: 1, ultima: 1 },
      pagina: 1,
      carregando: false,
      buscou: false,
      erro: null,
      snapshot: null,
      totalCarregado: 0,
    };
  },

  computed: {
    niveisRegiao() {
      const rotulos = {
        metropolitana: "Região metropolitana",
        saude: "Região de saúde",
        mesorregiao: "Mesorregião",
        macro: "Região do país",
      };
      return (this.opcoes.niveis_regiao || []).map((valor) => ({
        value: valor,
        text: rotulos[valor] || valor,
      }));
    },

    regioesDoNivel() {
      // O backend só devolve as listas de metropolitana e saúde (as que a tela
      // usa em combo); os demais níveis ficam como texto livre.
      return this.opcoes.regioes?.[this.filtros.nivel_regiao] || [];
    },

    snapshotLegivel() {
      if (!this.snapshot) return "";
      const [ano, mes] = this.snapshot.split("-");
      const meses = [
        "janeiro", "fevereiro", "março", "abril", "maio", "junho",
        "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
      ];
      return `${meses[Number(mes) - 1] || mes}/${ano}`;
    },
  },

  async created() {
    await this.carregarOpcoes();
  },

  methods: {
    async carregarOpcoes(uf) {
      try {
        this.opcoes = await carregarOpcoes(uf);
        this.snapshot = this.opcoes.snapshot;
        this.totalCarregado = this.opcoes.total_medicos || 0;
      } catch (e) {
        this.erro = "Não foi possível carregar as opções de filtro.";
      }
    },

    async aoTrocarUf(uf) {
      // Município e região pertencem à UF: trocar a UF sem limpá-los deixaria
      // um filtro impossível na tela (cidade de um estado, UF de outro).
      this.filtros.municipio = null;
      this.filtros.regiao = null;
      if (uf) await this.carregarOpcoes(uf);
    },

    async buscar(pagina = 1) {
      this.pagina = pagina;
      this.erro = null;
      this.carregando = true;

      try {
        const resultado = await buscarMedicos({ ...this.filtros, page: pagina });
        this.medicos = resultado.medicos;
        this.meta = resultado.meta;
        if (resultado.snapshot) this.snapshot = resultado.snapshot;
        this.buscou = true;
      } catch (e) {
        const status = e?.response?.status;
        this.medicos = [];
        this.meta = { total: 0, pagina: 1, ultima: 1 };
        this.erro =
          status === 422
            ? e?.response?.data?.message ||
              "Informe ao menos um filtro para buscar."
            : "Erro ao buscar médicos. Tente novamente.";
      } finally {
        this.carregando = false;
      }
    },

    limpar() {
      this.filtros = { ...FILTROS_VAZIOS };
      this.medicos = [];
      this.meta = { total: 0, pagina: 1, ultima: 1 };
      this.buscou = false;
      this.erro = null;
    },

    ddd(medico) {
      const local = (medico.locations || []).find((l) => l.ddd);
      return local ? local.ddd : null;
    },
  },
};
</script>
